import { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { useInstantSearch, usePagination } from 'react-instantsearch'
import { Hit, BaseHit } from 'instantsearch.js/es/types'
import { datadogRum } from '@datadog/browser-rum'
import Cookies from 'js-cookie'
import type { ProductReviewRating } from 'hpdp/services/reviews'
import { getProductsReviewsRating } from 'hpdp/services/reviews'

import { useUserResources } from 'shared/hooks'
import { Pagination } from 'shared/components/Pagination'
import { NewProductThumb } from 'shared/components/NewProductThumb'

import { WishlistContext } from 'site/Wishlist/context'
import { onProductClickDataLayer } from 'site/AlgoliaEntryKey/utils/data-layer'

import * as S from './styled'
import { ProductShell } from '../Shell'
import { useAlgoliaContext } from '../../context/algolia-context'
import { parsedProduct } from './parsedProduct'

type WishlistContextType = {
  isOnWishlist: (id: string) => boolean
}

interface ProductsProps {
  hits: Hit<BaseHit>[]
}

const Products = ({ hits }: ProductsProps) => {
  const {
    nbPages: totalPages,
    currentRefinement: currentPage,
    refine
  } = usePagination()
  const { status } = useInstantSearch()
  const { userResources } = useUserResources()
  const { isOnWishlist } = useContext(WishlistContext) as WishlistContextType
  const { gaTrack, type, query, baseUrl, isSearch, sendEventToAlgolia } =
    useAlgoliaContext()
  const isLoadingProducts = status === 'loading' || status === 'stalled'
  const [ratingResponse, setRatingResponse] = useState<ProductReviewRating[]>(
    []
  )
  const [isLoadingReview, setisLoadingReview] = useState(false)

  const shouldShowNewReviewsRating =
    window.env.feature.isNewReviewsSectionEnabled &&
    Cookies.get('new_reviews_ab_testing') === 'true'

  useEffect(() => {
    if (!shouldShowNewReviewsRating) {
      return
    }

    const fetchData = async () => {
      const ids = hits.map((hit: Hit<BaseHit>) => {
        const formattedProduct = parsedProduct(hit)

        return Number(formattedProduct.id)
      })

      try {
        setisLoadingReview(true)

        const response = await getProductsReviewsRating(ids)
        setRatingResponse(response)
      } catch (e) {
        datadogRum.addError(e)
      } finally {
        setisLoadingReview(false)
      }
    }

    if (hits.length >= 1) {
      fetchData()
    }
  }, [hits, shouldShowNewReviewsRating])

  const setPage = useCallback(
    (page: number) => {
      // The current page from `ALGOLIA` is 0-based, so we need to subtract 1 from it that we add at the moment we passed it as the current page to the Pagination component.
      refine(page - 1)
    },
    [refine]
  )

  const handleClick = (product: any, index: number) => {
    onProductClickDataLayer({
      product: product,
      region: userResources?.region,
      gaTrack,
      type,
      term: isSearch && query ? query : baseUrl,
      index,
      page: currentPage + 1,
      inWishlist: isOnWishlist(product.id)
    })
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [currentPage])

  if (isLoadingProducts) {
    return <ProductShell />
  }

  return (
    <Fragment>
      <S.Card>
        <S.ProductList>
          {hits.map((hit, index: number) => {
            const formattedProduct = parsedProduct(hit)
            return (
              <NewProductThumb
                onClick={() => {
                  handleClick(formattedProduct, index)
                  sendEventToAlgolia('click', hit, 'productListClick')
                }}
                variableWidth
                key={formattedProduct.id}
                product={formattedProduct}
                reviewRating={ratingResponse.find(
                  item => item.productLm === Number(formattedProduct.id)
                )}
                isLoadingReview={isLoadingReview}
              />
            )
          })}
        </S.ProductList>
      </S.Card>

      {totalPages > 1 && (
        <S.PaginationWrapper>
          <Pagination
            setPage={setPage}
            pageCount={totalPages}
            // The current page from `ALGOLIA` is 0-based, so we need to add 1 to it for the Pagination component work properly
            page={currentPage + 1}
          />
        </S.PaginationWrapper>
      )}
    </Fragment>
  )
}

export default Products
